import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { getStudentFees, payStudentFees, editStudentFees } from "../../../services/operations/studentFeesAPI";

const FeesProgress = ({ paid, total }) => {
  const percentage = total ? Math.min(100, Math.round((paid / total) * 100)) : 0;
  return (
    <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden relative">
      <div
        className="h-4 bg-green-500 transition-all duration-500"
        style={{ width: `${percentage}%` }}
      />
      <div className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-gray-700">
        {percentage}% Paid
      </div>
    </div>
  );
};

const FeeCard = ({ installment, onPay }) => {
  const isOverdue = new Date(installment.dueDate) < new Date() && installment.status !== "COMPLETED";
  
  const statusColors = {
    COMPLETED: "text-green-600 bg-green-100",
    PARTIAL: "text-yellow-600 bg-yellow-100",
    UNPAID: isOverdue ? "text-red-600 bg-red-100" : "text-gray-600 bg-gray-100",
  };

  return (
    <div className="flex justify-between items-center p-4 border rounded-xl shadow-sm bg-white">
      <div>
        <p className="font-semibold">{installment.title || `Installment ${installment.installmentNo}`}</p>
        <p className="text-sm text-gray-500">Due: {new Date(installment.dueDate).toLocaleDateString()}</p>
      </div>
      <div className="text-right space-y-1">
        <p className="font-medium">₹{installment.amount}</p>
        <div className="flex flex-col items-end gap-2">
          <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${statusColors[installment.status]}`}>
            {isOverdue && installment.status !== "COMPLETED" ? "OVERDUE" : installment.status}
          </span>
          {installment.status !== "COMPLETED" && (
            <button
              className="bg-blue-600 text-white text-xs px-3 py-1 rounded-md hover:bg-blue-700"
              onClick={() => onPay(installment)}
            >
              Pay Now
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const FeesDashboard = () => {
  const { studentId } = useParams();
  const [feesData, setFeesData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [payModalData, setPayModalData] = useState(null);
  const [editForm, setEditForm] = useState({});

  const fetchFees = async () => {
    setLoading(true);
    const res = await getStudentFees(studentId);
    if (res?.list?.length > 0) {
      setFeesData(res.list[0]);
    }
    setLoading(false);
  };

  useEffect(() => { fetchFees(); }, [studentId]);

  const openEditModal = () => {
    setEditForm({ ...feesData });
    setEditModalOpen(true);
  };

  const handleAddInstallment = () => {
    const nextNo = (editForm.installments?.length || 0) + 1;
    const newInst = { installmentNo: nextNo, amount: 0, dueDate: "", status: "UNPAID", paidAmount: 0 };
    setEditForm({ ...editForm, installments: [...(editForm.installments || []), newInst] });
  };

  const handleSaveEdit = async () => {
    const res = await editStudentFees({ ...editForm, studentFeesId: feesData._id });
    if (res) {
      setEditModalOpen(false);
      fetchFees();
    }
  };

  const handlePaySubmit = async (e) => {
    e.preventDefault();
    const amount = e.target.amount.value;
    const res = await payStudentFees({
      studentFeesId: feesData._id,
      installmentNo: payModalData.installmentNo,
      amount: Number(amount)
    });
    if (res) {
      setPayModalData(null);
      fetchFees();
    }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin" /></div>;
  if (!feesData) return <div className="text-center py-10">No records found.</div>;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">Fees Dashboard: {feesData.studentId?.name}</h1>
        <button onClick={openEditModal} className="bg-gray-800 text-white px-4 py-2 rounded-lg text-sm">Edit Structure</button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-white p-6 rounded-2xl shadow-sm border">
        <div><p className="text-gray-400 text-xs uppercase">Total</p><p className="text-xl font-bold">₹{feesData.totalFees}</p></div>
        <div><p className="text-gray-400 text-xs uppercase">Discount</p><p className="text-xl font-bold text-blue-600">{feesData.discount}%</p></div>
        <div><p className="text-gray-400 text-xs uppercase">Paid</p><p className="text-xl font-bold text-green-600">₹{feesData.paidAmount}</p></div>
        <div><p className="text-gray-400 text-xs uppercase">Balance</p><p className="text-xl font-bold text-red-600">₹{feesData.pendingAmount}</p></div>
        <div className="col-span-full pt-2"><FeesProgress paid={feesData.paidAmount} total={feesData.payableAmount} /></div>
      </div>

      <div className="space-y-4">
        <h2 className="text-lg font-bold">Payment Schedule</h2>
        <div className="grid gap-3">
          {feesData.installments?.map(inst => (
            <FeeCard key={inst.installmentNo} installment={inst} onPay={setPayModalData} />
          ))}
        </div>
      </div>

      {/* Edit Modal */}
      {editModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-xl font-bold mb-4">Edit Fee Structure</h2>
            <div className="grid grid-cols-2 gap-4 mb-6">
               <div><label className="text-xs font-bold">Base Fee</label>
               <input type="number" className="w-full border p-2 rounded" value={editForm.totalFees} onChange={e => setEditForm({...editForm, totalFees: e.target.value})} /></div>
               <div><label className="text-xs font-bold">Discount %</label>
               <input type="number" className="w-full border p-2 rounded" value={editForm.discount} onChange={e => setEditForm({...editForm, discount: e.target.value})} /></div>
            </div>
            
            <div className="space-y-3">
               <div className="flex justify-between items-center"><label className="font-bold">Installments</label>
               <button onClick={handleAddInstallment} className="text-blue-600 text-sm flex items-center gap-1"><Plus size={14}/> Add</button></div>
               {editForm.installments?.map((inst, i) => (
                 <div key={i} className="flex gap-2 items-end border-b pb-2">
                    <input type="text" placeholder="Title" className="border p-1 rounded text-sm flex-1" value={inst.title} onChange={e => {
                        const copy = [...editForm.installments]; copy[i].title = e.target.value; setEditForm({...editForm, installments: copy})
                    }} />
                    <input type="number" placeholder="Amount" className="border p-1 rounded text-sm w-24" value={inst.amount} onChange={e => {
                        const copy = [...editForm.installments]; copy[i].amount = e.target.value; setEditForm({...editForm, installments: copy})
                    }} />
                    <input type="date" className="border p-1 rounded text-sm" value={inst.dueDate?.split('T')[0]} onChange={e => {
                        const copy = [...editForm.installments]; copy[i].dueDate = e.target.value; setEditForm({...editForm, installments: copy})
                    }} />
                    <button onClick={() => {
                        const copy = editForm.installments.filter((_, idx) => idx !== i); setEditForm({...editForm, installments: copy})
                    }} className="text-red-500 p-1"><Trash2 size={16}/></button>
                 </div>
               ))}
            </div>
            <div className="flex justify-end gap-3 mt-6">
               <button onClick={() => setEditModalOpen(false)} className="px-4 py-2 text-gray-500">Cancel</button>
               <button onClick={handleSaveEdit} className="bg-blue-600 text-white px-6 py-2 rounded-lg">Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* Pay Modal */}
      {payModalData && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <form onSubmit={handlePaySubmit} className="bg-white p-6 rounded-xl w-80 space-y-4">
            <h3 className="font-bold text-lg">Pay {payModalData.title || `Installment ${payModalData.installmentNo}`}</h3>
            <p className="text-sm text-gray-500">Amount Due: ₹{payModalData.amount - payModalData.paidAmount}</p>
            <input name="amount" type="number" step="any" required defaultValue={payModalData.amount - payModalData.paidAmount} className="w-full border p-2 rounded" />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setPayModalData(null)} className="px-4 py-2">Cancel</button>
              <button type="submit" className="bg-green-600 text-white px-4 py-2 rounded-lg">Confirm Payment</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default FeesDashboard;